import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { UserRole } from '../api/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onInvited?: () => void;
  defaultRole?: UserRole;
  roles?: UserRole[];
  studentId?: number;
  studentName?: string;
}

export default function InviteDialog({
  open,
  onClose,
  onInvited,
  defaultRole = 'coach',
  roles = ['admin', 'coach', 'parent'],
  studentId,
  studentName,
}: Props) {
  const { toast } = useFeedback();
  const [role, setRole] = useState<UserRole>(defaultRole);
  const [email, setEmail] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [relationship, setRelationship] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRole(defaultRole);
      setEmail('');
      setFirst('');
      setLast('');
      setRelationship('');
      setError(null);
    }
  }, [open, defaultRole]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/invites', {
        email,
        role,
        first_name: first || undefined,
        last_name: last || undefined,
        student_id: studentId,
        relationship_label: relationship || undefined,
      });
      toast({ message: `Invite sent to ${email}`, severity: 'success' });
      onInvited?.();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send invite.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="invite-dialog-title">
      <DialogTitle id="invite-dialog-title">
        {studentName ? `Invite parent for ${studentName}` : 'Send invite'}
      </DialogTitle>
      <form onSubmit={onSubmit} aria-label="Invite form">
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Role"
              select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              disabled={!!studentId}
              inputProps={{ 'aria-label': 'Role' }}
            >
              {roles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              inputProps={{ 'aria-label': 'Email' }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="First name (optional)" value={first} onChange={(e) => setFirst(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Last name (optional)" value={last} onChange={(e) => setLast(e.target.value)} />
              </Grid>
            </Grid>
            {role === 'parent' && studentId && (
              <TextField
                label="Relationship (optional)"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                placeholder="e.g. Mother, Father, Guardian"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel invite">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Send invite">
            {busy ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
